import {GatewayType} from 'instruments/instrument-gateways/InstrumentGatewayConstants';
import {ValidationInfo} from 'schemas/tags/EditableSchemaTag';

export interface InstrumentAdapter {
  id: string;
  displayName: string;
  gatewayType?: GatewayType;
}

// Keep in sync with benchling_instruments_service_api_client.v1.models.InstrumentTypeResourceEnablementStatus, which
// is specified in services/instruments/client/openapi.yaml -- except for special "SELECTED" state
export type InstrumentAdapterEnablement = 'ENABLED' | 'DISABLED' | 'SELECTED';
export interface InstrumentAdapterConfiguration {
  id: string;
  displayName: string;
  enablementStatus: InstrumentAdapterEnablement;
}

export interface AdapterField {
  id: string;
  displayName: string;
  info: string | null;
  isRequired: boolean;
  isUneditable?: boolean;
  defaultValue?: string;
  fieldType?: string;
}

export enum ConnectionStatus {
  PENDING = 'PENDING',
  AVAILABLE = 'AVAILABLE',
  RUNNING = 'RUNNING',
  INSTRUMENT_ERROR = 'INSTRUMENT_ERROR',
  COMMUNICATION_ERROR = 'COMMUNICATION_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  DISABLED = 'DISABLED',
}

export enum ConnectionFieldType {
  TEXT = 'TEXT',
  SECRET_REF = 'SECRET_REF',
  PARAMETER = 'PARAMETER',
  GATEWAY = 'GATEWAY',
}

export interface ConnectionStatusResponse {
  value: ConnectionStatus;
  timestamp: string;
}

export interface InstrumentServiceDetails {
  id: string;
  adapterId: string;
  gatewayId: string;
  instrumentName: string;
  status: ConnectionStatusResponse;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  fields: {[id: string]: any};
  fieldValidationInfoByName: {[id: string]: ValidationInfo};
  allotropyVendor?: string;
  isFileSyncing?: boolean;
}

export enum InstrumentServiceLoadStatus {
  UNSET = 'UNSET',
  LOADING = 'LOADING',
  // This is the only status that should have data
  AVAILABLE = 'AVAILABLE',
  UNAVAILABLE = 'UNAVAILABLE',
}

export interface InstrumentServiceDetailsLoader {
  status: InstrumentServiceLoadStatus;
  data: InstrumentServiceDetails | null;
}

/**
 * Determines if a connection requires the user to log in before sending commands.
 *
 * Returns true if:
 * - per_user_login_required is 'true' AND
 * - Either username or password is not set on the connection
 *
 * If credentials are already configured on the connection, login is not required
 * even if per_user_login_required is true.
 */
export function connectionRequiresUserLogin(
  instrumentServiceDetails: InstrumentServiceDetails | null | undefined
): boolean {
  if (!instrumentServiceDetails) {
    return false;
  }
  const perUserLoginRequired = instrumentServiceDetails.fields.per_user_login_required?.value === 'true';
  if (!perUserLoginRequired) {
    return false;
  }
  // If credentials are set on the connection, no login prompt needed.
  // Check for null, undefined, empty string, and whitespace-only values.
  const username = instrumentServiceDetails.fields.username?.value?.trim();
  const password = instrumentServiceDetails.fields.password?.value?.trim();
  const hasCredentials = Boolean(username) && Boolean(password);
  return !hasCredentials;
}
